import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    checkEmailExists,
    sendPasswordResetLink
} from '../../../../app/store/slices/auth/authSlice';
import { getFirebaseErrorMessage } from '../../firebase-error-codes/errorCodes';
import { trimAndCleanSpaces } from '../../../../utils/trimAndCleanSpaces';

/**
 * @fileoverview Handles the forgot password functionality, validating emails and initiating password reset process.
 * Manages form state, validates email existence, and sends reset links through Firebase authentication.
 *
 * @module useForgotPassword
 * @requires react-redux
 * @requires formik
 * @requires yup
 */

/**
 * Custom hook for handling forgot password workflow.
 * @returns {Object} Object containing formik instance with validation and submission logic
 */
export default function useForgotPassword() {
    const dispatch = useDispatch();
    const selector = useSelector((state) => state.auth);
    const [isLinkSent, setIsLinkSent] = React.useState(false);

    const formik = useFormik({
        initialValues: {
            email: '' || selector?.email
        },
        validationSchema: Yup.object({
            email: Yup.string().email('Invalid email address').required('Email is required')
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setFieldError }) => {
            try {
                // Step 1: Clean and trim the email input
                const cleanEmail = trimAndCleanSpaces(values.email).toLowerCase();

                // Step 2: Validate the cleaned email
                if (!cleanEmail) {
                    setFieldError('email', 'Email is required');
                    return;
                }

                // Step 3: Check if the email exists in the database
                const emailCheckResult = await dispatch(checkEmailExists(cleanEmail));

                // Step 4: Store the response from the email check result
                const emailExists = emailCheckResult.payload?.exist;

                // Step 5: Handle errors from the email check
                if (emailCheckResult?.error?.message === 'Rejected') {
                    setFieldError('email', 'Something went wrong! Please try again.');
                    formik.setSubmitting(false);
                    formik.setFieldTouched('email', true, false);
                    return;
                }

                // Step 6: If email does not exist, set an error message
                if (!emailExists) {
                    setFieldError('email', 'Email does not exist. Please check your email.');
                    return;
                }

                // Step 7: If email exists, proceed to send the password reset link
                const response = await dispatch(sendPasswordResetLink(cleanEmail));

                // Step 8: Handle the response from sending the reset link
                if (response?.error?.message === 'Rejected') {
                    setFieldError('email', response?.payload);
                    return;
                }

                // Step 9: If the reset link is sent successfully, update the state
                if (response?.payload?.status === true) {
                    setIsLinkSent(true);
                }
            } catch (firebaseError) {
                // Step 10: Handle any unexpected errors from Firebase and set a generic error message
                let errorMessage = 'Server Error';
                errorMessage = getFirebaseErrorMessage(firebaseError) || errorMessage;
                setFieldError('email', errorMessage);
            }
        }
    });

    // return needed values and functions
    return {
        formik,
        isLinkSent
    };
}
